import { Button, Flex, Text } from "@chakra-ui/react";
import { FieldPreviewProps } from "../../types";
import { SOURCE } from "components/containers/InspectionFormat/Content/SettingPinLayoutFrame/context/pinLayoutFrameReducer";
import { DroppableBox } from "components/containers/InspectionFormat/containers/LayoutFrameBuilder";
import FieldDevicePreviewWrapper from "../../../components/FieldDevicePreviewWrapper";
import React, { memo, useCallback, useEffect } from "react";
import RecoveryIcon from "components/icon/RecoveryIcon";
import ConnectDeviceIcon from "components/icon/ConnectDeviceIcon";
import { PressureValue } from "libs/peripheral/type";
import { usePeripheral } from "../../../context/PeripheralProvider";

export enum PressureOptKey {
  START_DATE = "startDate",
  START_VALUE = "startValue",
  END_DATE = "endDate",
  END_VALUE = "endValue",
}

export const pressureOptions = [
  { value: PressureOptKey.START_DATE, label: "開始時刻" },
  { value: PressureOptKey.START_VALUE, label: "開始値" },
  { value: PressureOptKey.END_DATE, label: "終了時刻" },
  { value: PressureOptKey.END_VALUE, label: "終了値" },
];

interface DevicePressurePreviewerProps
  extends FieldPreviewProps<PressureValue> {}

const DevicePressurePreviewer: React.FC<DevicePressurePreviewerProps> = ({
  item,
  value,
  isReadOnly,
  isLoading,
  isDraggable,
  index,
  prevItemResultCount,
  allItemResultCount,
  onChange,
}) => {
  const { pressure, onPressure } = usePeripheral();

  const onClickRequest = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      if (isReadOnly) return;
      pressure(item.id);
    },
    [isReadOnly, item.id, pressure]
  );

  useEffect(() => {
    const unsubscribe = onPressure?.(item.id, (data) => {
      onChange?.(data);
    });

    return () => {
      unsubscribe?.();
    };
  }, [onPressure, item.id, onChange]);

  if (isDraggable) {
    return (
      <FieldDevicePreviewWrapper
        title={item.name}
        data-type={item.dataType}
        index={index}
        prevItemResultCount={prevItemResultCount}
        allItemResultCount={allItemResultCount}
      >
        <Flex position={"relative"} flexDir={"column"} gap="1rem">
          {pressureOptions.map(({ value, label }) => {
            return (
              <DroppableBox
                key={value}
                name={label}
                icon={<ConnectDeviceIcon w="3rem" h="3rem" />}
                id={item.id}
                extraId={value}
                source={SOURCE.INSPECTION_FORM}
              >
                <Text
                  color={"#525252"}
                  w="12rem"
                  fontWeight={500}
                  fontSize={"1.4rem"}
                  flex={"0 0 12rem"}
                  maxW={"12rem"}
                  p="0.5rem 1rem"
                >
                  {label}
                </Text>
                <Text
                  p="0.5rem 1rem"
                  flexGrow={1}
                  textAlign={"right"}
                  color="#A3A3A3"
                >
                  --
                </Text>
              </DroppableBox>
            );
          })}
        </Flex>
      </FieldDevicePreviewWrapper>
    );
  }

  return (
    <FieldDevicePreviewWrapper
      title={item.name}
      index={index}
      prevItemResultCount={prevItemResultCount}
      allItemResultCount={allItemResultCount}
    >
      <Flex alignItems={"center"} gap="1rem">
        <Flex alignItems={"center"} gap="0.4rem" pl="1rem">
          <ConnectDeviceIcon w="2.2rem" h="2.2rem" />
          <Text fontSize={"1.2rem"} fontWeight={700} color="#171717">
            みるみるくん 圧力試験Ⅲ
          </Text>
        </Flex>
        <Button
          ml="auto"
          onClick={onClickRequest}
          variant="filled"
          isDisabled={isLoading || isReadOnly}
          fontSize={"1.2rem"}
          fontWeight={"normal"}
          gap="0.2rem"
          minH={"4rem"}
          leftIcon={<RecoveryIcon w="2.2rem" h="2.2rem" />}
        >
          機器から <br /> 情報取得
        </Button>
      </Flex>
      <Flex
        position={"relative"}
        flexDir={"column"}
        mt="1rem"
        gap="1rem"
        pl="5rem"
      >
        {pressureOptions.map((option) => {
          return (
            <Flex key={option.value} alignItems={"center"}>
              <Text
                color={"#525252"}
                w="12rem"
                fontWeight={500}
                fontSize={"1.4rem"}
                flex={"0 0 12rem"}
                maxW={"12rem"}
              >
                {option.label}
              </Text>
              <Text
                fontSize={"1.6rem"}
                flexGrow={1}
                textAlign={"right"}
                color="#A3A3A3"
                whiteSpace={"nowrap"}
              >
                {value?.[option.value] ?? "--"}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </FieldDevicePreviewWrapper>
  );
};

export default memo(DevicePressurePreviewer);
